import { styled } from 'dripsy';
import { ImagePickerOptions } from 'expo-image-picker';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { ModalComponentProp, useModal } from 'react-native-modalfy';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { IS_MOBILE, IS_WEB } from '@utils';
import { BaseText } from '../../components/common/BaseText';
import { Icon } from '../../components/common/Icon';
import { IconTouchable } from '../../components/common/IconTouchable';
import { Touchable } from '../../components/common/Touchable';
import { ModalView } from './components/ModalView';
import { Separator } from '../common/Separator';
import { modals } from '../../modals/modals';
import { ModalStackParamsType } from '../../modals/types';
import { ImagePickerResultType } from '../../services/ImagePickerService';
import { BottomModalView } from './components/BottomModalView';

const ModalViewContainer = IS_MOBILE ? BottomModalView : ModalView;

const CrossIconContainer = styled(View)({
  position: 'absolute',
  zIndex: 1,
  elevation: 1,
  top: '$6',
  right: IS_WEB ? '$6' : '$9',
});

const Container = styled(View)({
  pl: IS_WEB ? '$7' : '$9',
  pr: IS_WEB ? '$12' : '$9',
});

const TouchableContainer = styled(Touchable)({
  flexDirection: 'row',
  alignItems: 'center',
  mt: IS_WEB ? '$0' : '$6',
  py: '$6',
});

interface ITextStyleProps {
  red?: boolean;
}

const Text = styled(BaseText)((props: ITextStyleProps) => ({
  color: props.red ? '$error300' : '$heading800',
  variant: 'text.medium',
  ml: '$5',
}));

export interface IImagePickerModalParams {
  onOpenPicker: (
    options?: ImagePickerOptions,
  ) => Promise<ImagePickerResultType | void>;
  onOpenCamera: (
    options?: ImagePickerOptions,
  ) => Promise<ImagePickerResultType | void>;
  onClose: () => void;
  onRemoveImage?: () => void;
  isRemoveImageAllowed?: boolean;
}

type ModalPropsType = ModalComponentProp<
  ModalStackParamsType,
  void,
  'ImagePickerModal'
>;

export function ImagePickerModal({ modal }: ModalPropsType) {
  const { bottom } = useSafeAreaInsets();
  const { t } = useTranslation();
  const { openModal, closeModal, closeAllModals } =
    useModal<ModalStackParamsType>();

  if (!modal.params) {
    return null;
  }

  const {
    onOpenCamera,
    onOpenPicker,
    onRemoveImage,
    onClose,
    isRemoveImageAllowed,
  } = modal.params;

  function closeModalByCrossIcon() {
    closeModal();
    onClose();
  }

  function removeCurrentImage() {
    if (onRemoveImage) {
      onRemoveImage();
      onClose();
      closeAllModals();
    }
  }

  function openImageRemoveModal() {
    openModal(modals.ConfirmationModal, {
      title: t('common.imagePicker.deleteTitle'),
      subtitle: t('common.imagePicker.deleteSubtitle'),
      buttons: [
        { text: t('common.imagePicker.delete') },
        { text: t('common.imagePicker.cancel') },
      ],
      onConfirm: removeCurrentImage,
    });
  }

  function openCamera() {
    onOpenCamera();
    closeModal();
  }

  function openPicker() {
    onOpenPicker();
    closeModal();
  }

  return (
    <ModalViewContainer bottomInset={bottom}>
      <CrossIconContainer>
        <IconTouchable
          onPress={closeModalByCrossIcon}
          name="close-light"
          size={24}
        />
      </CrossIconContainer>

      <Separator size="$4" />
      <Container>
        {isRemoveImageAllowed && (
          <TouchableContainer onPress={openImageRemoveModal}>
            <Icon name="delete-light" color="$error300" size={24} />
            <Text red>{t('common.imagePicker.removePhoto')}</Text>
          </TouchableContainer>
        )}

        {IS_MOBILE && (
          <TouchableContainer onPress={openCamera}>
            <Icon name="camera-light" size={24} />
            <Text>{t('common.imagePicker.takePhoto')}</Text>
          </TouchableContainer>
        )}

        <TouchableContainer onPress={openPicker}>
          <Icon name="paper-upload-light" size={24} />
          <Text>{t('common.imagePicker.pickImage')}</Text>
        </TouchableContainer>
      </Container>
      <Separator size={IS_WEB ? '$4' : '$6'} />
    </ModalViewContainer>
  );
}
